import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Badge } from '../../components/ui/Badge';
import {
  getManufacturerProfile,
  getAllEngagements,
  getAllBrands,
  getAllPaymentReceipts,
  getAllDesigns,
} from '../../store/actions';
import { DESIGN_STATUS, RECEIPT_STATUS, formatINR, initials, label } from '../../constants/garmentCodes';
import { MonthlyLedgerChart, RecentActivity } from '../../components/dashboard/DashboardWidgets';

const RECEIPT_SUBMITTED = 2;
const DESIGN_AWAITING_CHARGE = 1;
const isLow = (eng) => eng.lowBalanceThreshold != null && (eng.availableBalance ?? 0) < eng.lowBalanceThreshold;

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

export default function ManufacturerDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state.auth?.loginUser);
  const profile = useSelector((state) => state.manufacturer.profile);
  const dashboard = useSelector((state) => state.manufacturer.dashboard);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brands = useSelector((state) => state.brand.brands);
  const pendingReceipts = useSelector((state) => state.paymentreceipt.receipts);
  const pendingDesigns = useSelector((state) => state.design.designs);

  useEffect(() => {
    dispatch(getManufacturerProfile({ withDashboard: true }));
    dispatch(getAllEngagements());
    dispatch(getAllBrands());
    dispatch(getAllPaymentReceipts({ status: RECEIPT_SUBMITTED }));
    dispatch(getAllDesigns({ status: DESIGN_AWAITING_CHARGE }));
  }, [dispatch]);

  const engagementById = Object.fromEntries(engagements.map((e) => [String(e.id), e]));
  const kpis = [
    { label: 'Brands', value: dashboard?.partnerCount ?? '—', sub: `${brands.length} on record`, link: '/dashboard/brands', color: 'border-l-blue-500' },
    { label: 'Engagements', value: dashboard?.engagementCount ?? '—', sub: 'all engagements', link: '/dashboard/engagements', color: 'border-l-blue-500' },
    { label: 'Total Wallet Balance', value: formatINR(dashboard?.walletBalance), sub: 'across all engagements', link: '/dashboard/wallets', color: 'border-l-green-500' },
    { label: 'Available Balance', value: formatINR(dashboard?.availableBalance), sub: `${formatINR(dashboard?.heldAmount)} held`, link: '/dashboard/wallets', color: 'border-l-green-500' },
    { label: 'Pending Receipts', value: pendingReceipts.length, sub: 'awaiting review', link: '/dashboard/receipts', color: 'border-l-amber-500' },
    { label: 'Design Charges Pending', value: pendingDesigns.length, sub: 'awaiting brand approval', link: '/dashboard/designs', color: 'border-l-amber-500' },
    { label: 'Orders', value: dashboard?.orderCount ?? '—', sub: 'all orders', link: '/dashboard/orders', color: 'border-l-blue-500' },
    { label: 'Requirements', value: dashboard?.requirementCount ?? '—', sub: 'all requirements', link: '/dashboard/requirements', color: 'border-l-blue-500' },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title={`${greeting()}${loginUser?.name ? `, ${loginUser.name}` : ''}`} breadcrumbs={[{ workspace: true }, { label: 'Dashboard' }]} />

      <main className="flex-1 overflow-auto p-6 pb-20 md:pb-6">
        <p className="text-[13px] text-gray-500 mb-6">
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        {!profile && (
          <div className="mb-6 flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-[12px] text-blue-800">
            <span>Your manufacturer profile is not set up yet.</span>
            <button onClick={() => navigate('/dashboard/settings')} className="font-semibold hover:underline">Set up profile →</button>
          </div>
        )}

        {engagements.map((eng) =>
          isLow(eng) ? (
            <div key={eng.id} className="mb-3 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="text-amber-600 text-base mt-0.5">⚠</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-amber-800">Low wallet balance — {eng.brandName}</p>
                <p className="text-[12px] text-amber-700 mt-0.5">
                  Available balance is {formatINR(eng.availableBalance)}, below the {formatINR(eng.lowBalanceThreshold)} threshold.
                </p>
              </div>
              <button onClick={() => navigate(`/dashboard/wallets?engagementId=${eng.id}`)} className="text-[11px] text-amber-800 font-semibold whitespace-nowrap hover:underline">View Wallet →</button>
            </div>
          ) : null,
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 mt-3">
          {kpis.map((card) => (
            <button key={card.label} onClick={() => navigate(card.link)} className={`bg-white rounded-lg border border-gray-100 border-l-4 ${card.color} p-4 text-left hover:shadow-sm transition-shadow`}>
              <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{card.label}</div>
              <div className="text-[22px] font-bold text-gray-900 font-mono leading-tight">{card.value}</div>
              <div className="text-[11px] text-gray-400 mt-1">{card.sub}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <MonthlyLedgerChart months={dashboard?.monthlyLedger} />

            <div className="bg-white rounded-lg border border-gray-100">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
                <h3 className="text-[13px] font-semibold text-gray-900">Engagement Wallets</h3>
                <button onClick={() => navigate('/dashboard/engagements')} className="text-[11px] text-blue-600 hover:underline">All engagements →</button>
              </div>
              <div className="divide-y divide-gray-50">
                {engagements.length === 0 && <p className="px-5 py-6 text-center text-[12px] text-gray-400">No engagements yet</p>}
                {engagements.map((eng) => {
                  const pct = eng.walletBalance ? Math.min(100, Math.round(((eng.availableBalance || 0) / eng.walletBalance) * 100)) : 0;
                  const low = isLow(eng);
                  return (
                    <div key={eng.id} className="px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/dashboard/wallets?engagementId=${eng.id}`)}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-[12px] font-medium text-gray-800">{eng.brandName || eng.name}</span>
                          {low && <span className="ml-2 text-[10px] text-amber-600 font-medium">⚠ Low balance</span>}
                        </div>
                        <span className="text-[12px] font-semibold font-mono text-gray-800">{formatINR(eng.availableBalance)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${low ? 'bg-amber-400' : 'bg-green-400'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-gray-400">{formatINR(eng.heldAmount)} held</span>
                        <span className="text-[10px] text-gray-400">{formatINR(eng.walletBalance)} total</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-gray-100">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-[13px] font-semibold text-gray-900">Action Required</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {pendingReceipts.length === 0 && pendingDesigns.length === 0 && <p className="px-5 py-6 text-center text-[12px] text-gray-400">Nothing pending</p>}
                {pendingReceipts.map((r) => (
                  <div key={`r-${r.id}`} className="px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/dashboard/receipts')}>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-gray-700">{r.receiptNumber || `Receipt #${r.id}`} — {engagementById[String(r.engagementId)]?.brandName || ''}</span>
                      <Badge status={label(RECEIPT_STATUS, r.status)} />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">Payment receipt · {formatINR(r.amount)}</p>
                  </div>
                ))}
                {pendingDesigns.map((d) => (
                  <div key={`d-${d.id}`} className="px-5 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/dashboard/designs')}>
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] text-gray-700">{d.name} — {d.brandName}</span>
                      <Badge status={label(DESIGN_STATUS, d.status)} />
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">Design charge · {formatINR(d.charge)}</p>
                  </div>
                ))}
              </div>
            </div>

            <RecentActivity items={dashboard?.recentActivity} basePath="/dashboard" />

            <div className="bg-white rounded-lg border border-gray-100">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
                <h3 className="text-[13px] font-semibold text-gray-900">Brands</h3>
                <button onClick={() => navigate('/dashboard/brands')} className="text-[11px] text-blue-600 hover:underline">All brands →</button>
              </div>
              <div className="divide-y divide-gray-50">
                {brands.length === 0 && <p className="px-5 py-6 text-center text-[12px] text-gray-400">No brands yet</p>}
                {brands.slice(0, 6).map((b) => (
                  <div key={b.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/dashboard/engagements?brandId=${b.id}`)}>
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 flex-shrink-0">{initials(b.name)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-medium text-gray-800">{b.name}</span>
                        {b.lowBalance && <span className="text-[9px] text-amber-600 font-medium">LOW</span>}
                      </div>
                      <span className="text-[10px] text-gray-400">{b.activeEngagements ?? 0} engagement{b.activeEngagements === 1 ? '' : 's'}</span>
                    </div>
                    <Badge status={b.status || '—'} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
